import { Box, HStack, Text } from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import { useCommittedPathname } from '@/components/nav/useCommittedPathname'
import { useDispatch } from 'react-redux'
import { openMenuOverlay } from '@/store/slices/uiSlice'
import { SignUpPromptModal } from '@/modals/auth'
import { glassCardProps } from '@/styles/glassCard'
import { navItems, isNavItemActive } from '@/components/nav/navItems'
import { useNavGateBehaviors } from '@/components/nav/useNavGateBehaviors'

export function MainNavigationTabBar() {
  const pathname = useCommittedPathname()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { isQuizTaking, isSignUpModalOpen, setIsSignUpModalOpen, handleNavItemClick } = useNavGateBehaviors()

  const handleTabClick = (to: string, label: string) => {
    if (!handleNavItemClick(to, label)) {
      return
    }

    // Menu is not a route destination (D45b Phase 3) — it opens the shared
    // glass overlay instead of navigating.
    if (to === '/menu') {
      dispatch(openMenuOverlay())
      return
    }

    navigate({ to })
  }

  if (isQuizTaking) {
    return null
  }

  return (
    <>
    <SignUpPromptModal
      isOpen={isSignUpModalOpen}
      onClose={() => setIsSignUpModalOpen(false)}
      context="navigation"
    />
    <HStack
      // Excluded from the page-transition root snapshot — see
      // `data-app-chrome` in variables.css.
      data-app-chrome="tab-bar"
      // bg intentionally stays var(--color-overlay-90) (a dark scrim), not
      // glassCardProps.bg — this bar is `position: fixed; bottom: 0` with
      // arbitrary page content scrolling underneath (any authenticated/
      // public-with-tabs route) and needs real darkening for icon/text
      // legibility in light mode, unlike roster members that sit over the
      // app's own dark background. Matches AppHeader.tsx / FilterBar.tsx /
      // InfoPopover.tsx's carve-out (D45b Phase 6, FR-N9) — only the
      // blur/saturate/brightness recipe
      // (`glassCardProps.backdropFilter`) is shared, not `glassCardProps.bg`.
      // The tint uses InfoPopover's overlay-90 (not FilterBar's overlay-60)
      // because this bar's existing icon/label colors (`purple.400`,
      // `gray.400`) are mid-tone — verified via contrast math that
      // overlay-60 leaves them at ~1.8–2.3:1 against a white page (fails
      // AA 4.5:1; confirmed live via axe, D45b review round-3), while
      // overlay-90 clears ~5.4–6.9:1 for the same colors.
      bg="var(--color-overlay-90)"
      backdropFilter={glassCardProps.backdropFilter}
      borderTop="1px solid"
      borderColor="whiteAlpha.200"
      position="fixed"
      bottom="0"
      left="0"
      right="0"
      zIndex="sticky"
      borderRadius="0"
      sx={{
        WebkitBackdropFilter: glassCardProps.backdropFilter,
        '@supports not (backdrop-filter: blur(1px))': {
          bg: 'blackAlpha.900',
          backdropFilter: 'none',
        },
      }}
      pb="env(safe-area-inset-bottom)"
      justify="space-around"
    >
      {navItems.map(({ to, icon: Icon, label }) => {
        const isActive = isNavItemActive(to, pathname)
        return (
          <Box
            as="button"
            key={to}
            onClick={() => handleTabClick(to, label)}
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            minH="44px"
            minW="44px"
            px={3}
            py={2}
            bg="transparent"
            border="none"
            outline="none"
            color={isActive ? 'purple.400' : 'gray.400'}
            filter={isActive ? 'drop-shadow(0 0 6px var(--chakra-colors-purple-400))' : 'none'}
            transition="color 0.15s ease, filter 0.15s ease"
            _hover={{ color: isActive ? 'purple.300' : 'gray.300' }}
            _active={{ transform: 'scale(0.95)' }}
            // D45b Phase 6 a11y sweep: outline="none" above left this
            // control with no visible focus indicator — same gap found and
            // fixed on DesktopNavRail.tsx and AppHeader.tsx's avatar
            // button, the other two nav-bars roster members.
            _focusVisible={{ boxShadow: '0 0 0 2px var(--color-overlay-90), 0 0 0 4px var(--color-primary-purple)' }}
            sx={{
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Icon size={20} />
            <Text fontSize="xs" fontWeight={isActive ? 'semibold' : 'normal'}>
              {label}
            </Text>
          </Box>
        )
      })}
    </HStack>
    </>
  )
}

export default MainNavigationTabBar
